/**
 * Time, shared by the two things in this app that mark one.
 *
 * `map/layers/ticks.ts` marks a **frame slider**, whose track is index-spaced:
 * the gap between two frames is not the same everywhere, and the honest
 * rendering puts every frame the same distance apart. `chart/ticks.ts` marks a
 * **chart axis**, which is time-spaced. They cannot share a function — the
 * positions mean different things — but the round-interval table and the
 * day-boundary emphasis are one idea, and a second copy of that is the
 * `GROUP_LABELS` bug again. So the table lives here and both import it.
 *
 * The zone helpers are here for the same reason they exist at all: the chart's
 * x axis is in the **point's** zone rather than the viewer's, because a
 * diurnal temperature curve read against the wrong midnight is a chart that
 * lies about night. Everything else in the app keeps the viewer's zone, and
 * that inconsistency is deliberate.
 */

export const MINUTE = 60_000
export const HOUR = 60 * MINUTE
export const DAY = 24 * HOUR

/**
 * Candidate scale intervals, coarsest-last, in minutes.
 *
 * Every model axis this app draws is a divisor of one of these: 15 min
 * (sub-hourly HRRR), 1 h (everything else to +48 h), 3 h (GFS and NBM past
 * their hourly segment), 6 h (NBM past +192 h).
 */
export const INTERVAL_MINUTES = [15, 30, 60, 180, 360, 720, 1440, 2880] as const

/** The same table in milliseconds, which is what both tick modules measure in. */
export const INTERVALS: readonly number[] = INTERVAL_MINUTES.map((m) => m * MINUTE)

/**
 * The coarsest interval that keeps about `target` marks across `spanMs`.
 *
 * The last entry is the fallback rather than an error: an axis spanning more
 * than `target` days is a deployment that raised
 * `WEATHER_MODEL_MAX_LEAD_MINUTES`, and a crowded scale beats none.
 */
export function chooseInterval(spanMs: number, target: number): number {
  return INTERVALS.find((ms) => spanMs / ms <= target) ?? INTERVALS[INTERVALS.length - 1]
}

// ---------- zones ----------

/**
 * How many minutes ahead of UTC `tz` is at `date`, or 0 for an unknown zone.
 *
 * Computed per instant rather than once, because the answer changes at a DST
 * boundary — which is exactly the kind of day a meteogram is most likely to be
 * read on, and exactly the kind of bug that never shows up in a test written
 * in July.
 */
export function zoneOffsetMinutes(date: Date, tz: string | null | undefined): number {
  if (!tz) return -date.getTimezoneOffset()
  const parts = zoneParts(date, tz)
  if (!parts) return -date.getTimezoneOffset()
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  )
  // The instant's own seconds, so a sub-second difference cannot round the
  // offset to a minute that does not exist.
  return Math.round((asUtc - Math.floor(date.getTime() / 1000) * 1000) / MINUTE)
}

interface ZoneParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

const partsCache = new Map<string, Intl.DateTimeFormat>()

function formatter(tz: string): Intl.DateTimeFormat | null {
  const cached = partsCache.get(tz)
  if (cached) return cached
  try {
    const made = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    partsCache.set(tz, made)
    return made
  } catch {
    // An unknown zone string from a third party. The viewer's zone is the
    // honest fallback and the panel says which zone it is drawing in.
    return null
  }
}

function zoneParts(date: Date, tz: string): ZoneParts | null {
  const fmt = formatter(tz)
  if (!fmt) return null
  const found: Record<string, string> = {}
  for (const part of fmt.formatToParts(date)) found[part.type] = part.value
  return {
    year: Number(found.year),
    month: Number(found.month),
    day: Number(found.day),
    // `hour12: false` spells midnight as 24 in some engines.
    hour: Number(found.hour) % 24,
    minute: Number(found.minute),
    second: Number(found.second),
  }
}

/** The instant shifted so that UTC arithmetic on it is local arithmetic. */
export function asZoneLocal(date: Date, tz: string | null | undefined): number {
  return date.getTime() + zoneOffsetMinutes(date, tz) * MINUTE
}

/** Whether this instant is local midnight — the axis's most useful cue. */
export function isDayStart(date: Date, tz: string | null | undefined): boolean {
  const local = asZoneLocal(date, tz)
  return local % DAY === 0
}

/**
 * The zone's short name (`MDT`), for the line above the axis.
 *
 * Named rather than assumed: the chart is in the *point's* zone and every
 * other panel is in the viewer's, so the one that is different has to say so.
 */
export function zoneLabel(date: Date, tz: string | null | undefined): string {
  if (!tz) return ''
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    }).formatToParts(date)
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
  } catch {
    return ''
  }
}

/** Whether a zone names a different wall clock from the viewer's, right now. */
export function differsFromViewer(date: Date, tz: string | null | undefined): boolean {
  if (!tz) return false
  return zoneOffsetMinutes(date, tz) !== -date.getTimezoneOffset()
}

/** `5 PM`, `Sun`, `Sun 5 PM` — in `tz`, for an axis label. */
export function formatInZone(
  date: Date,
  tz: string | null | undefined,
  options: Intl.DateTimeFormatOptions,
): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      ...options,
      ...(tz ? { timeZone: tz } : {}),
    })
      .format(date)
      .replace(/ /g, ' ')
  } catch {
    return new Intl.DateTimeFormat(undefined, options).format(date).replace(/ /g, ' ')
  }
}
